import type {
  ButtonHTMLAttributes,
  ComponentProps,
  ForwardedRef,
  ReactNode,
  Ref,
} from 'react';
import type { To } from 'react-router';

import { Link } from '@sorare/routing';

import { useClickHandler } from '#src/hooks/useClickHandler.ts';

type NativeButtonProps = ButtonHTMLAttributes<
  HTMLButtonElement | HTMLAnchorElement
> & {
  disableDebounce?: boolean;
  href?: string;
  externalLink?: boolean;
  ref?: Ref<HTMLButtonElement | HTMLAnchorElement>;
};

type LinkButtonProps = {
  externalLink?: boolean;
  /** Only meaningful on the native-button path; Link navigates on its own. */
  disableDebounce?: boolean;
} & Omit<ComponentProps<typeof Link>, 'to'>;

type AllButtonProps = Omit<NativeButtonProps, 'ref'> &
  Omit<LinkButtonProps, 'ref' | 'type'> & {
    ref?: Ref<HTMLButtonElement | HTMLAnchorElement>;
  };

const NativeButtonBase = (props: NativeButtonProps) => {
  const {
    onClick,
    disableDebounce = true,
    type = 'button',
    href,
    externalLink,
    children,
    ref,
    ...rest
  } = props;
  const clickHandler = useClickHandler(onClick, disableDebounce);

  if (href != null) {
    return (
      <a
        ref={ref as ForwardedRef<HTMLAnchorElement>}
        href={href}
        onClick={clickHandler}
        {...(externalLink
          ? ({ target: '_blank', rel: 'noopener noreferrer' } as any)
          : {})}
        {...rest}
      >
        {children}
      </a>
    );
  }

  return (
    <button
      ref={ref as ForwardedRef<HTMLButtonElement>}
      onClick={clickHandler}
      /* oxlint-disable react/button-has-type */
      type={type}
      {...rest}
    >
      {children}
    </button>
  );
};

export const ButtonBase: {
  (args: { to: To } & LinkButtonProps): ReactNode;
  (
    args: { to?: never; keepSearchParams?: never } & NativeButtonProps
  ): ReactNode;
  (args: { to?: To } & AllButtonProps): ReactNode;
} = ({ keepSearchParams, disableDebounce, ...props }) => {
  // oxlint-disable-next-line sorare/destructuring-assignment
  if ('to' in props && props.to != null) {
    const { externalLink, ref, ...rest } = props;
    return (
      <Link
        ref={ref as ForwardedRef<HTMLAnchorElement>}
        keepSearchParams={keepSearchParams}
        {...rest}
        {...(externalLink
          ? ({ target: '_blank', rel: 'noopener noreferrer' } as any)
          : {})}
      />
    );
  }

  const { ref, ...rest } = props;
  return (
    <NativeButtonBase
      {...rest}
      disableDebounce={disableDebounce}
      ref={ref as ForwardedRef<HTMLButtonElement>}
    />
  );
};
